import { useState, useEffect, useCallback } from 'react';
import { assemblyService } from 'services/api';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import SearchableSelect from 'components/SearchableSelect';
import AvisoModal from 'components/AvisoModal';
import styles from './EleccionesButton.module.css';

// ── Elecciones (Ola 2) — cargos + postulaciones + elección ────────────────────
const POS_STATUS = {
  borrador:      { label: 'Borrador',        color: '#8a92a6' },
  postulaciones: { label: 'Postulaciones',   color: '#f79009' },
  en_eleccion:   { label: 'Elección activa', color: '#7c3aed' },
  cerrado:       { label: 'Cerrado',         color: '#12b76a' },
};
// Rol que ocupa el ganador en el acta: al generarla, presidente y secretario
// se prellenan con el ganador del cargo cerrado que tenga ese rol.
const ROLES_ACTA = [
  { value: '',           label: 'Sin rol en el acta' },
  { value: 'presidente', label: 'Presidente en el acta' },
  { value: 'secretario', label: 'Secretario en el acta' },
];
const NOM_STATUS = {
  pendiente:  { label: 'Pendiente', color: '#f79009' },
  aceptada:   { label: 'Aceptada',  color: '#12b76a' },
  rechazada:  { label: 'Rechazada', color: '#f04438' },
};

function CandidateRow({ pos, nom, busy, soloLectura, onAccept, onReject, onRemove }) {
  const ns = NOM_STATUS[nom.status] || NOM_STATUS.pendiente;
  const editable = !soloLectura && ['borrador', 'postulaciones'].includes(pos.status);
  return (
    <div className={styles.candidato}>
      <i className={`fas ${nom.won ? 'fa-trophy' : nom.added_by_admin ? 'fa-user-tie' : 'fa-user'} ${nom.won ? styles.candidatoGanador : styles.candidatoIcono}`} />
      <div className={styles.candidatoTexto}>
        <div className={styles.candidatoNombre}>
          {nom.candidate_name}
          {nom.added_by_admin && <span className={styles.agregado}>(agregado)</span>}
        </div>
        {nom.motivacion && <div className={styles.motivacion}>{nom.motivacion}</div>}
        {pos.status === 'cerrado' && nom.result_votes != null && (
          <div className={styles.votos}>
            {nom.result_votes} voto{nom.result_votes !== 1 ? 's' : ''} · coef {Number(nom.result_coef || 0).toFixed(2)}
          </div>
        )}
      </div>
      {pos.status === 'cerrado'
        ? <span className={`badge ${nom.won ? styles.etiquetaGano : styles.etiquetaResultado}`}>{nom.won ? 'GANÓ' : '—'}</span>
        : <span className={`badge ${styles.etiquetaPostulacion}`} style={{ '--tono': ns.color, '--fondo': `${ns.color}22` }}>{ns.label}</span>}
      {editable && nom.status !== 'aceptada' && (
        <button className="btn btn-sm btn-success btn-icon" disabled={busy} title="Aceptar" onClick={() => onAccept(nom.id)}>
          <i className="fas fa-check" />
        </button>
      )}
      {editable && nom.status === 'pendiente' && (
        <button className="btn btn-sm btn-danger btn-icon" disabled={busy} title="Rechazar" onClick={() => onReject(nom.id)}>
          <i className="fas fa-xmark" />
        </button>
      )}
      {editable && nom.status !== 'pendiente' && (
        <button className="btn btn-sm btn-secondary btn-icon" disabled={busy} title="Quitar candidato" onClick={() => onRemove(nom.id)}>
          <i className="fas fa-trash" />
        </button>
      )}
    </div>
  );
}

function PositionCard({ assembly, pos, busy, reload, setError }) {
  const [addName,  setAddName]  = useState('');
  const [addMot,   setAddMot]   = useState('');
  const [showAdd,  setShowAdd]  = useState(false);
  const [confirmDelPos, setConfirmDelPos] = useState(false);
  const [deletingPos,   setDeletingPos]   = useState(false);
  const st  = POS_STATUS[pos.status] || POS_STATUS.borrador;
  const asmEnCurso  = assembly.status === 'en_progreso';
  // Asamblea finalizada = el acta: el servidor ya rechaza cambiar sus cargos, candidatos y postulaciones (PR 498); aquí solo se muestran, sin controles de edición.
  const soloLectura = assembly.status === 'finalizada';
  const aceptados   = pos.nominations.filter(n => n.status === 'aceptada').length;
  const puedeAbrir  = pos.status === 'postulaciones' && asmEnCurso && aceptados >= 1;

  const call = async (fn) => { try { await fn(); await reload(); } catch (e) { setError(e.response?.data?.message || 'No se pudo completar la acción.'); } };

  const confirmDeletePos = async () => {
    setDeletingPos(true);
    await call(() => assemblyService.deletePosition(assembly.id, pos.id));
    setDeletingPos(false);
    setConfirmDelPos(false);
  };

  return (
    <div className={styles.cargo}>
      <div className={styles.cargoCabecera}>
        <i className={`fas fa-check-to-slot ${styles.iconoCargo}`} />
        <div className={styles.cargoTexto}>
          <div className={styles.cargoNombre}>{pos.name}</div>
          <div className={styles.cargoMeta}>
            <span>{pos.seats} puesto{pos.seats !== 1 ? 's' : ''}</span>
            <SearchableSelect value={pos.rol_acta || ''} onChange={(v) => call(() => assemblyService.updatePosition(assembly.id, pos.id, { rol_acta: v }))} disabled={busy || soloLectura} auto allowEmpty={false} minWidth={0} maxWidth={220} ariaLabel="Rol en el acta" triggerClassName={styles.selectorRol}
                  options={ROLES_ACTA.map(r => ({ value: r.value, label: r.label }))} />
          </div>
        </div>
        <span className={`badge ${styles.etiquetaCargo}`} style={{ '--tono': st.color, '--fondo': `${st.color}22` }}>{st.label}</span>
        {!soloLectura && ['borrador', 'cerrado'].includes(pos.status) && (
          <button className="btn btn-sm btn-danger btn-icon" disabled={busy} title="Eliminar cargo"
            onClick={() => setConfirmDelPos(true)}>
            <i className="fas fa-trash" />
          </button>
        )}
      </div>

      {/* Candidatos */}
      {pos.nominations.length > 0 && (
        <div className={styles.candidatos}>
          {pos.nominations.map(nom => (
            <CandidateRow key={nom.id} pos={pos} nom={nom} busy={busy} soloLectura={soloLectura}
              onAccept={(id) => call(() => assemblyService.setNominationStatus(assembly.id, pos.id, id, 'aceptada'))}
              onReject={(id) => call(() => assemblyService.setNominationStatus(assembly.id, pos.id, id, 'rechazada'))}
              onRemove={(id) => call(() => assemblyService.deleteNomination(assembly.id, pos.id, id))} />
          ))}
        </div>
      )}

      {/* Candidato manual */}
      {!soloLectura && ['borrador', 'postulaciones'].includes(pos.status) && (
        showAdd ? (
          <div className={styles.agregarCaja}>
            <input className={`form-control ${styles.campoChico}`} placeholder="Nombre del candidato (puede ser externo)" maxLength={150}
              value={addName} onChange={e => setAddName(e.target.value)} />
            <input className={`form-control ${styles.campoChico}`} placeholder="Motivación (opcional)" maxLength={2000}
              value={addMot} onChange={e => setAddMot(e.target.value)} />
            <div className={styles.agregarBotones}>
              <button className="btn btn-sm btn-primary" disabled={busy}
                onClick={() => {
                  // Antes `disabled={!addName.trim()}` era el único gate — un
                  // clic sin nombre no hacía nada y no mostraba error.
                  if (!addName.trim()) return setError('El nombre del candidato es obligatorio.');
                  call(async () => {
                    await assemblyService.addCandidate(assembly.id, pos.id, { candidate_name: addName.trim(), motivacion: addMot.trim() });
                    setAddName(''); setAddMot(''); setShowAdd(false);
                  });
                }}>
                <i className="fas fa-plus" /> Agregar
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowAdd(false)}>Cancelar</button>
            </div>
          </div>
        ) : (
          <button className={`btn btn-sm btn-secondary ${styles.botonAgregar}`} onClick={() => setShowAdd(true)}>
            <i className="fas fa-user-plus" /> Agregar candidato
          </button>
        )
      )}

      {/* Acciones de estado */}
      {!soloLectura && pos.status === 'borrador' && (
        <div className={styles.nota}>
          <i className={`fas fa-eye-slash ${styles.iconoNota}`} />
          <span>Los propietarios <strong>aún no ven</strong> este cargo. Pulsa <strong>“Abrir postulaciones”</strong> para publicarlo y que puedan postularse.</span>
        </div>
      )}
      <div className={styles.acciones}>
        {!soloLectura && pos.status === 'borrador' && (
          <button className="btn btn-sm btn-primary" disabled={busy}
            onClick={() => call(() => assemblyService.positionStatus(assembly.id, pos.id, 'abrir_postulaciones'))}>
            <i className="fas fa-bullhorn" /> Abrir postulaciones
          </button>
        )}
        {!soloLectura && pos.status === 'postulaciones' && (
          <>
            <button className="btn btn-sm btn-secondary" disabled={busy}
              onClick={() => call(() => assemblyService.positionStatus(assembly.id, pos.id, 'cerrar_postulaciones'))}>
              <i className="fas fa-lock" /> Cerrar postulaciones
            </button>
            <button className="btn btn-sm btn-primary" disabled={busy || !puedeAbrir}
              title={!asmEnCurso ? 'La asamblea debe estar en progreso' : aceptados < 1 ? 'Acepta al menos un candidato' : 'Genera la votación'}
              onClick={() => call(() => assemblyService.openElection(assembly.id, pos.id))}>
              <i className="fas fa-check-to-slot" /> Abrir elección
            </button>
          </>
        )}
        {!soloLectura && pos.status === 'en_eleccion' && (
          <>
            <span className={styles.votacionActiva}>
              <i className={`fas fa-circle ${styles.punto}`} /> Votación activa en la sala
            </span>
            <button className="btn btn-sm btn-danger" disabled={busy}
              onClick={() => call(() => assemblyService.closeElection(assembly.id, pos.id))}>
              <i className="fas fa-flag-checkered" /> Cerrar elección
            </button>
          </>
        )}
        {soloLectura && ['borrador', 'postulaciones'].includes(pos.status) && (
          <span className={styles.nota}>No se llegó a abrir la elección de este cargo.</span>
        )}
        {pos.status === 'cerrado' && (() => {
          const won = pos.nominations.filter(n => n.won);
          const libres = pos.seats - won.length;
          return (
            <span className={styles.ganadores}>
              <i className={`fas fa-trophy ${styles.iconoGanadores}`} />
              {won.map(n => n.candidate_name).join(', ') || 'Sin ganadores'}
              {libres > 0 && <span className={styles.sinProveer}> · {libres} puesto{libres !== 1 ? 's' : ''} sin proveer</span>}
            </span>
          );
        })()}
      </div>

      {confirmDelPos && (
        <ConfirmDeleteModal title="Eliminar cargo" onClose={() => setConfirmDelPos(false)} onConfirm={confirmDeletePos} deleting={deletingPos}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar el cargo <span className={styles.confirmaNombre}>{pos.name}</span>?
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}

export default function EleccionesButton({ assembly }) {
  const [open,      setOpen]      = useState(false);
  const [positions, setPositions] = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState('');
  const [newName,   setNewName]   = useState('');
  const [newSeats,  setNewSeats]  = useState(1);
  const [newRol,    setNewRol]    = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await assemblyService.positions(assembly.id); setPositions(r.data.positions || []); }
    catch (err) { setError(err.response?.data?.message || 'No se pudieron cargar las elecciones.'); }
    finally { setBusy(false); }
  }, [assembly.id]);

  useEffect(() => { if (open) { setPositions(null); setError(''); load(); } }, [open, load]);

  const createPos = async () => {
    // Antes esta condición solo se usaba para el `disabled` del botón — un
    // clic sin nombre no hacía nada y no mostraba ningún mensaje (mismo
    // hallazgo que en ComunicadoFormModal).
    if (!newName.trim()) return setError('El nombre del cargo es obligatorio.');
    setBusy(true); setError('');
    try {
      await assemblyService.createPosition(assembly.id, { name: newName.trim(), seats: Number(newSeats) || 1, rol_acta: newRol });
      setNewName(''); setNewSeats(1); setNewRol(''); await load();
    } catch (err) { setError(err.response?.data?.message || 'No se pudo crear el cargo.'); }
    finally { setBusy(false); }
  };

  const total = positions === null ? (assembly.positions_count || 0) : positions.length;

  return (
    <>
      <button className={`btn btn-sm btn-secondary btn-icon ${total ? styles.botonConCargos : ''}`} onClick={() => setOpen(true)}
        title={total ? `Elecciones (${total})` : 'Elecciones y cargos'} aria-label="Elecciones">
        <i className="fas fa-check-to-slot" />
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className={`modal ${styles.modalElecciones}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className={`fas fa-check-to-slot ${styles.iconoTitulo}`} />
                Elecciones — Asamblea #{assembly.number_assembly}</h3>
              <button className="btn-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* El aviso va ARRIBA del cuerpo, que es lo que se desplaza, y los botones que lo provocan están más abajo (en cada cargo): en un
                  celular quedaba fuera de la vista. Es la pieza compartida (fija arriba, fondo sólido, role="alert"); al tocarlo se cierra. */}
              {error && <AvisoModal onClick={() => setError('')}>{error}</AvisoModal>}

              {/* Nuevo cargo (no con la asamblea finalizada) */}
              {assembly.status !== 'finalizada' && <div className={styles.filaNuevoCargo}>
                <input className={`form-control ${styles.campoCargo}`} placeholder="Nuevo cargo (ej. Consejo de Administración)" maxLength={150}
                  value={newName} onChange={e => setNewName(e.target.value)} />
                <input className={`form-control ${styles.campoPuestos}`} type="number" min="1" max="15" title="Puestos"
                  value={newSeats} onChange={e => setNewSeats(e.target.value)} />
                <SearchableSelect value={newRol} onChange={(v) => setNewRol(v)} auto allowEmpty={false} minWidth={0} maxWidth={240} ariaLabel="Rol en el acta"
                  options={ROLES_ACTA.map(r => ({ value: r.value, label: r.label }))} />
                <button className="btn btn-primary" disabled={busy} onClick={createPos}>
                  <i className="fas fa-plus" />
                </button>
              </div>}

              {assembly.status === 'finalizada' && (
                <div className={styles.aviso}>
                  <i className={`fas fa-lock ${styles.iconoAviso}`} />
                  La asamblea ya finalizó: las elecciones son las del acta y no se pueden cambiar. Si hay que corregirlas, reinicia la asamblea.
                </div>
              )}

              {assembly.status === 'pendiente' && (
                <div className={styles.aviso}>
                  <i className={`fas fa-info-circle ${styles.iconoAviso}`} />
                  Puedes armar los cargos y candidatos ahora; la elección solo se abre con la asamblea <strong>en progreso</strong>.
                </div>
              )}

              {positions === null ? (
                <div className={styles.cargando}><div className={`spinner ${styles.spinnerCarga}`} /></div>
              ) : positions.length === 0 ? (
                <div className={styles.sinCargos}>
                  Aún no hay cargos para elegir en esta asamblea.
                </div>
              ) : (
                <div className={styles.listaCargos}>
                  {positions.map(pos => (
                    <PositionCard key={pos.id} assembly={assembly} pos={pos} busy={busy} reload={load} setError={setError} />
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
